// Neo-soul instruments (docs/AUDIO_DESIGN.md §12), used only by neosoul.js: a Wurlitzer electric piano (a struck steel reed
// read by an electrostatic pickup, through its little amp's tremolo and speaker, plus a slight chorus), a warm fingered bass
// that slides into its notes and sits behind the beat, a lazy dusty kit (the kick a touch early, a wide snare, loose hats) and
// a breathy "ooh" voice or a mellow synth lead. The Wurlitzer allocates an oscillator, a gain and a WaveShaper a note (cap
// with stealing); bass, kit and lead are persistent and gated with setTargetAtTime (a note-on cancels what the last left).
import { clamp, hash } from '../../engine/core.js';
import { mtof, hold, noise } from '../dsp.js';
import { LEVELS, gain, filter, osc, panner, persistent } from '../instruments.js';

// Level knobs (the harness zeroes them to measure one part alone).
export const NS_LEVELS = { wurli: 1, bass: 1, kit: 1, lead: 1 };
// The mix balance against the jazzhop parts they replace (Rhodes, bass, kit, lead).
const MIXLV = { wurli: 0.33, bass: 0.62, kit: 0.8, lead: 1.1 };
const TWO_PI = Math.PI * 2;
const delay = (ac, t, max = 0.1) => { const d = ac.createDelay(max); d.delayTime.value = t; return d; };

/* ---------------- Wurlitzer: reed → pickup → soft clip per note; speaker EQ → tremolo → chorus on the bus ---------------- */
// The reed's motion through the pickup: a PeriodicWave 1 · .3 · .14 · .06 (the pickup's own harmonics, the 2nd strongest;
// no DC), then a symmetric soft clip y = tanh(kx)/k (k 3, unity slope at 0) on the reed's amplitude. Odd harmonics grow with
// it, so a harder strike barks and every note mellows as it decays (C4, 20–120 ms after the strike: v .45 → 3rd −14,
// 5th −31, 7th −37 dB under the fundamental; v .8 → −11, −24, −29).
const K = 3;
const SHARED = new WeakMap();                                     // ac → { wave, curve }
function reedParts(ac) {
  if (!SHARED.has(ac)) {
    const wave = ac.createPeriodicWave(new Float32Array(5), new Float32Array([0, 1, 0.3, 0.14, 0.06]));
    const curve = new Float32Array(2049);
    for (let i = 0; i < curve.length; i++) curve[i] = Math.tanh(K * (i / 1024 - 1)) / K;
    SHARED.set(ac, { wave, curve });
  }
  return SHARED.get(ac);
}

/**
 * createWurli(ac, out, send, p, opts) → { note, chord, stats, dispose }   (the style's `ep`)
 * p: { lp (the speaker LP, Hz), trem: { rate, depth } | null, chorus: { rate, cents }, drive }. opts: { registry, tag, cap, r, t0, log }.
 * Note: reed amplitude A = 1.4·drive·v^1.3·(330/f)^.2 (.75–1.15), a 1.8 ms strike, down to .62 A (τ 75 ms: the bark
 * fades), then τ 1.25·(262/f)^.45 s (.45–2.4) from 140 ms; the damper τ relTau at note-off (the final chord's .9 s rings).
 * A key struck again lets its old note go (τ 12 ms); a full pool steals the oldest (10 ms).
 * Bus: HP 130 Hz (the small speakers' thin bottom) → peaking 1.2 kHz +6 dB (Q .6, their nasal honk) → LP p.lp (Q .7) → the
 * amp's tremolo (a sine at p.trem.rate, ±depth/2, no pan: the 200A's is mono) → chorus (dry .8 + delays 7 / 10.5 ms swept
 * in antiphase by a sine at p.chorus.rate, ±cents, wet .28 at ∓.55) → level → out, reverb .22. Each chord knocks the key
 * bed: noise → BP 750 Hz, τ 5 ms, 0.035v, into the speaker.
 */
export function createWurli(ac, out, send, p, { registry, tag, cap = 16, r = Math.random, t0 = ac.currentTime, log = null }) {
  const { wave, curve } = reedParts(ac), nodes = [], srcs = [], N = x => (nodes.push(x), x);
  const busIn = N(gain(ac, 1)), hp = N(filter(ac, 'highpass', 130, 0.7)), honk = N(filter(ac, 'peaking', 1200, 0.6, 6));
  const lp = N(filter(ac, 'lowpass', p.lp, 0.7)), trem = N(gain(ac, p.trem ? 1 - p.trem.depth / 2 : 1)), fxIn = N(gain(ac, 1));
  busIn.connect(hp); hp.connect(honk); honk.connect(lp); lp.connect(trem); trem.connect(fxIn);
  if (p.trem) {
    const lfo = N(osc(ac, 'sine', p.trem.rate)), lfoG = N(gain(ac, p.trem.depth / 2));
    lfo.connect(lfoG); lfoG.connect(trem.gain); srcs.push(lfo);
  }
  // chorus: ±c ¢ at rate f is a sine sweep of c / (1731·2π·f) s
  const rate = p.chorus.rate, dev = p.chorus.cents / (1731 * TWO_PI * rate);
  const dry = N(gain(ac, 0.8)), dL = N(delay(ac, 0.007)), dR = N(delay(ac, 0.0105)), wL = N(gain(ac, 0.28)), wR = N(gain(ac, 0.28));
  const pL = N(panner(ac, -0.55)), pR = N(panner(ac, 0.55)), cho = N(osc(ac, 'sine', rate)), choL = N(gain(ac, dev)), choR = N(gain(ac, -dev));
  const outG = N(gain(ac, MIXLV.wurli * NS_LEVELS.wurli)), sendG = N(gain(ac, 0.22));
  fxIn.connect(dry); dry.connect(outG);
  fxIn.connect(dL); dL.connect(wL); wL.connect(pL); pL.connect(outG);
  fxIn.connect(dR); dR.connect(wR); wR.connect(pR); pR.connect(outG);
  cho.connect(choL); choL.connect(dL.delayTime); cho.connect(choR); choR.connect(dR.delayTime);
  outG.connect(out); outG.connect(sendG); sendG.connect(send);
  srcs.push(cho);
  // the key bed: one looped noise source for the track, gated per chord
  const nz = N(ac.createBufferSource()), bed = N(filter(ac, 'bandpass', 750, 1.2)), bedG = N(gain(ac, 0));
  nz.buffer = noise(ac, 'white'); nz.loop = true;
  nz.connect(bed); bed.connect(bedG); bedG.connect(busIn);
  srcs.push(nz);
  const bus = persistent(registry, tag, srcs, nodes, t0, 0.7);
  let voices = [], maxActive = 0, steals = 0, notes = 0;           // { v, midi, end (sounding end), rec }
  const cut = (x, t, tau) => { registry.stop(x.v, t, tau); x.end = t; if (x.rec) x.rec[1] = Math.max(x.rec[0], Math.min(x.rec[1], t)); };

  function note(t, midi, vel, dur, relTau = 0.08) {
    voices = voices.filter(x => x.end > t);
    const k = voices.findIndex(x => x.midi === midi);
    if (k >= 0) cut(voices.splice(k, 1)[0], t, 0.012);            // the key is struck again: its old note lets go
    else if (voices.length >= cap) { cut(voices.shift(), t, 0.01); steals++; }
    const f = mtof(midi) * Math.pow(2, (r() * 5 - 2.5) / 1200);     // ±2.5 ¢: reeds tuned by hand with solder
    const o = osc(ac, 'sine', f), env = gain(ac, 0), sh = ac.createWaveShaper();
    o.setPeriodicWave(wave); sh.curve = curve;
    o.connect(env); env.connect(sh); sh.connect(busIn);
    const A = 1.4 * (p.drive || 1) * Math.pow(vel, 1.3) * clamp(Math.pow(330 / f, 0.2), 0.75, 1.15), off = t + Math.max(0.05, dur);
    env.gain.setValueAtTime(0, t);
    env.gain.linearRampToValueAtTime(A, t + 0.0018);
    env.gain.setTargetAtTime(0.62 * A, t + 0.0018, 0.075);
    if (off > t + 0.14) env.gain.setTargetAtTime(0, t + 0.14, clamp(1.25 * Math.pow(262 / f, 0.45), 0.45, 2.4));
    env.gain.setTargetAtTime(0, off, relTau);
    const end = off + Math.max(0.3, relTau * 7);
    o.start(t); o.stop(end);
    const v = registry.add({ src: [o], all: [o, env, sh], start: t, end, tag, out: env });
    const x = { v, midi, end: off + relTau * 4, rec: log ? [t, off + relTau * 4] : null };
    if (x.rec) log.push(x.rec);
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
  }
  return {
    note,
    /** bottom to top over `strum` (a hand's slight roll, ±15 %), the top voice +10 %, the left-hand root −20 % */
    chord(t, midis, vel, dur, strum = 0.012, relTau = 0.08) {
      const n = midis.length;
      hold(bedG.gain, t); bedG.gain.setTargetAtTime(0.035 * vel, t, 0.0006); bedG.gain.setTargetAtTime(0, t + 0.002, 0.005);
      midis.forEach((m, k) => {
        const dt = n > 1 ? (k * strum / (n - 1)) * (0.85 + 0.3 * r()) : 0;
        note(t + dt, m, Math.min(1, vel * (k === n - 1 && n > 1 ? 1.1 : k === 0 && n > 2 ? 0.8 : 1)), dur - dt, relTau);
      });
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) { bus.dispose(t); },
  };
}

/* ---------------- fingered bass: a flatwound string, a plucked LP, legato slides, behind the beat ---------------- */
/** p = plan.ns.bass: { lp (Hz), lag (s) }. Harmonics 1 · .5 · .3 · .16 · .08 · .04 · .02 → LP (Q .6) opening to 2.6 × p.lp on
 *  the pluck (1.5 × on a legato note) and back with τ 90 ms → peaking 150 Hz +1.5 dB (the body) → peaking 750 Hz +2.5 dB (the
 *  finger on the string: it carries on phones) → amp: τ 6 ms (a legato note τ 20 ms, from where the last one is), to .65
 *  (τ .35 s), a long ring (τ 1.6 s from .45 s), release τ 60 ms. Every note sits p.lag late. slide: from the last note when
 *  it is still ringing (within 60 ms of its release: portamento τ 45 ms), else up from a whole step below (τ 35 ms). */
export function createSoulBass(ac, out, p, { registry, tag, t0 = ac.currentTime }) {
  const wave = ac.createPeriodicWave(new Float32Array(8), new Float32Array([0, 1, 0.5, 0.3, 0.16, 0.08, 0.04, 0.02]));
  const o = osc(ac, 'sine', 55), lp = filter(ac, 'lowpass', p.lp, 0.6), body = filter(ac, 'peaking', 150, 0.9, 1.5);
  const fing = filter(ac, 'peaking', 750, 1, 2.5), amp = gain(ac, 0);
  o.setPeriodicWave(wave);
  o.connect(lp); lp.connect(body); body.connect(fing); fing.connect(amp); amp.connect(out);
  const P = persistent(registry, tag, [o], [o, lp, body, fing, amp], t0);
  let lastF = 0, lastOff = -1;
  return {
    note(t, midi, vel, dur, slide = false) {
      t += p.lag;
      const f = mtof(midi), off = t + Math.max(0.08, dur), legato = lastF > 0 && t < lastOff + 0.06;
      for (const prm of [o.frequency, lp.frequency, amp.gain]) hold(prm, t);
      if (slide) {
        o.frequency.setValueAtTime(legato ? lastF : f * Math.pow(2, -2 / 12), t);
        o.frequency.setTargetAtTime(f, t + 0.004, legato ? 0.045 : 0.035);
      } else o.frequency.setValueAtTime(f, t);
      lp.frequency.setTargetAtTime((legato ? 1.5 : 2.6) * p.lp * (0.8 + 0.4 * vel), t, 0.004);
      lp.frequency.setTargetAtTime(p.lp, t + 0.012, 0.09);
      const A = 0.5 * vel * MIXLV.bass * NS_LEVELS.bass;
      amp.gain.setTargetAtTime(legato ? 0.85 * A : A, t, legato ? 0.02 : 0.006);
      amp.gain.setTargetAtTime(0.65 * A, t + 0.04, 0.35);
      if (off > t + 0.45) amp.gain.setTargetAtTime(0, t + 0.45, 1.6);
      amp.gain.setTargetAtTime(0, off, 0.06);
      lastF = f; lastOff = off;
    },
    dispose(t) { P.dispose(t); },
  };
}

/* ---------------- lazy kit: an early round kick, a wide snare, loose hats, a cross-stick (one noise source) ---------------- */
/** kit = plan.drums.kit: { kickF0, kickF1, kickDecay, early, snareF, snareDecay, wide, hatHP, hatClosed, loose, rimF, lp }.
 *  kick: sine kickF0 (105–125) → kickF1 (45–51) Hz (τ 40 ms), 0.55v, attack τ 2 ms, decay τ kickDecay (.17–.24 s); a felt
 *        thump (noise → LP 500 Hz, 0.06v, τ 7 ms). Off-beat kicks land `early` (6–14 ms) ahead, the beats a third of it.
 *  snare: triangle 195 → 172 Hz (τ 30 ms) 0.15v, τ 50 ms + noise → HP 500 → BP snareF (1.5–2 kHz, Q .5) 0.36v, τ snareDecay
 *        (.1–.15 s; ghost 35 ms), reverb .3; wide: the noise again 11 and 17 ms later at `wide` (.25–.4), panned ∓.75.
 *  hat: noise → HP hatHP (5–6.4 kHz), 0.16v, τ hatClosed (16–26 ms; open .16 s), pan +.22, reverb .06; each hit moves by
 *        up to ±loose (3–8 ms) and ×.82–1.18 in level, from a hash of its time (the same for every listener).
 *  rim: sine rimF (480–560) .6 + triangle 2.4·rimF .3 → BP 1.3 kHz (Q .9), 0.28v, τ 12 ms, pan −.18, reverb .25.
 *  all through the bus LP kit.lp. */
export function createSoulKit(ac, out, send, kit, { registry, tag, t0 = ac.currentTime, stepDur = 0.2 }) {
  const L = LEVELS.kit * MIXLV.kit * NS_LEVELS.kit, nodes = [], N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5));
  bus.connect(out);
  const kOsc = N(osc(ac, 'sine', kit.kickF1)), kAmp = N(gain(ac, 0)), kLP = N(filter(ac, 'lowpass', 500, 0.7)), kThump = N(gain(ac, 0));
  kOsc.connect(kAmp); kAmp.connect(bus); src.connect(kLP); kLP.connect(kThump); kThump.connect(bus);
  const sOsc = N(osc(ac, 'triangle', 172)), sTone = N(gain(ac, 0)), sSend = N(gain(ac, 0.3));
  const sHP = N(filter(ac, 'highpass', 500, 0.7)), sBP = N(filter(ac, 'bandpass', kit.snareF, 0.5)), sNoise = N(gain(ac, 0));
  sOsc.connect(sTone); sTone.connect(bus); sTone.connect(sSend);
  src.connect(sHP); sHP.connect(sBP); sBP.connect(sNoise); sNoise.connect(bus); sNoise.connect(sSend); sSend.connect(send);
  for (const [d, pan] of [[0.011, -0.75], [0.017, 0.75]]) {
    const dl = N(delay(ac, d, 0.05)), g = N(gain(ac, kit.wide)), pn = N(panner(ac, pan));
    sNoise.connect(dl); dl.connect(g); g.connect(pn); pn.connect(bus);
  }
  const hHP = N(filter(ac, 'highpass', kit.hatHP, 0.7)), hAmp = N(gain(ac, 0)), hPan = N(panner(ac, 0.22)), hSend = N(gain(ac, 0.06));
  src.connect(hHP); hHP.connect(hAmp); hAmp.connect(hPan); hPan.connect(bus); hPan.connect(hSend); hSend.connect(send);
  const r1 = N(osc(ac, 'sine', kit.rimF)), r2 = N(osc(ac, 'triangle', 2.4 * kit.rimF)), rg1 = N(gain(ac, 0.6)), rg2 = N(gain(ac, 0.3));
  const rBP = N(filter(ac, 'bandpass', 1300, 0.9)), rAmp = N(gain(ac, 0)), rPan = N(panner(ac, -0.18)), rSend = N(gain(ac, 0.25));
  r1.connect(rg1); r2.connect(rg2); rg1.connect(rBP); rg2.connect(rBP); rBP.connect(rAmp); rAmp.connect(rPan);
  rPan.connect(bus); rPan.connect(rSend); rSend.connect(send);
  const p = persistent(registry, tag, [src, kOsc, sOsc, r1, r2], nodes, t0, 2.1);
  const hit = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };
  const stepOf = e => (e ? ((Math.round(e.t / stepDur) % 16) + 16) % 16 : 0);
  return {
    kick(t, v, e) {
      const early = kit.early * (stepOf(e) % 4 ? 1 : 0.33);
      if (t - early > ac.currentTime + 0.002) t -= early;
      kOsc.frequency.setValueAtTime(kit.kickF0, t); kOsc.frequency.setTargetAtTime(kit.kickF1, t, 0.04);
      hit(kAmp.gain, 0.55 * v * L, t, 0.002, 0.008, kit.kickDecay);
      hit(kThump.gain, 0.06 * v * L, t, 0.0008, 0.002, 0.007);
    },
    snare(t, v, ghost = false) {
      sOsc.frequency.setValueAtTime(195, t); sOsc.frequency.setTargetAtTime(172, t, 0.03);
      hit(sTone.gain, 0.15 * v * L, t, 0.001, 0.004, 0.05);
      hit(sNoise.gain, 0.36 * v * L, t, 0.0015, 0.005, ghost ? 0.035 : kit.snareDecay);
    },
    hat(t, v, open = false, e) {
      const k = e ? Math.round(e.t * 1e4) : 0, u = hash(k, 91), w = hash(k, 92);
      hit(hAmp.gain, 0.16 * v * L * (0.82 + 0.36 * w), Math.max(ac.currentTime, t + (u - 0.5) * 2 * kit.loose), 0.0008, 0.003, open ? 0.16 : kit.hatClosed);
    },
    rim(t, v) { hit(rAmp.gain, 0.28 * v * L, t, 0.0006, 0.002, 0.012); },
    dispose(t) { p.dispose(t); },
  };
}

/* ---------------- lead: a breathy "ooh" voice or a mellow synth, mono, a quarter-note echo ---------------- */
/** lead = plan.lead ({ voice 'ooh' | 'mellow', pan }), p = plan.ns.lead ({ vib (¢), glide (τ s), scoop (odds) }).
 *  ooh: sine f + triangle f (.2) + a double-tracked sine at +7 ¢ (.45) → LP 1.8 kHz (Q .5), breath noise → BP 1.6 kHz (Q .8):
 *  a puff (.09) settling to .03 of the note; attack τ 45 ms (a glide τ 30 ms), to .85 (τ .5 s), release τ .16 s; a note
 *  that isn't legato scoops up from −80 ¢ (odds p.scoop, τ 60 ms); legato notes glide (τ p.glide); vibrato 5.1 Hz ±p.vib ¢ from
 *  .25 s, in over .3 s. mellow: triangle + saw (.12) → LP 1.5 kHz (Q .7), attack τ 25 ms, release τ .12 s, vibrato 4.6 Hz
 *  from .3 s, glides. Echo 4 steps (fb .28, LP 2 kHz, wet .3), reverb .45. */
export function createSoulLead(ac, out, send, lead, p, { registry, tag, stepDur, t0 = ac.currentTime, r = Math.random }) {
  const nodes = [], src = [], N = x => (nodes.push(x), x);
  const amp = N(gain(ac, 0)), pan = N(panner(ac, lead.pan || 0)), sendG = N(gain(ac, 0.45));
  const dly = N(delay(ac, Math.min(1.9, 4 * stepDur), 2)), dLP = N(filter(ac, 'lowpass', 2000, 0.5)), fb = N(gain(ac, 0.28)), wet = N(gain(ac, 0.3));
  amp.connect(pan); amp.connect(dly); dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wet); wet.connect(pan);
  pan.connect(out); pan.connect(sendG); sendG.connect(send);
  const L = MIXLV.lead * NS_LEVELS.lead, ooh = lead.voice !== 'mellow';
  const vib = N(osc(ac, 'sine', ooh ? 5.1 : 4.6)), vibG = N(gain(ac, 0)), lp = N(filter(ac, 'lowpass', ooh ? 1800 : 1500, ooh ? 0.5 : 0.7));
  vib.connect(vibG); lp.connect(amp);
  const oscs = [], br = { g: null };
  if (ooh) {
    const s1 = N(osc(ac, 'sine', 440)), s2 = N(osc(ac, 'triangle', 440)), g2 = N(gain(ac, 0.2)), s3 = N(osc(ac, 'sine', 440)), g3 = N(gain(ac, 0.45));
    s3.detune.value = 7;
    s1.connect(lp); s2.connect(g2); g2.connect(lp); s3.connect(g3); g3.connect(lp);
    const nz = N(ac.createBufferSource()), bp = N(filter(ac, 'bandpass', 1600, 0.8)), bg = N(gain(ac, 0));
    nz.buffer = noise(ac, 'white'); nz.loop = true;
    nz.connect(bp); bp.connect(bg); bg.connect(amp);
    oscs.push(s1, s2, s3); src.push(s1, s2, s3, nz); br.g = bg;
  } else {
    const tri = N(osc(ac, 'triangle', 440)), saw = N(osc(ac, 'sawtooth', 440)), sg = N(gain(ac, 0.12));
    tri.connect(lp); saw.connect(sg); sg.connect(lp);
    oscs.push(tri, saw); src.push(tri, saw);
  }
  for (const o of oscs) vibG.connect(o.detune);
  src.push(vib);
  const P = persistent(registry, tag, src, nodes, t0, 1.9);
  let lastEnd = -1;
  return {
    note(t, midi, vel, dur, glide = false) {
      dur = Math.max(0.06, dur);
      const f = mtof(midi), A = (ooh ? 0.34 : 0.3) * vel * L, legato = glide && t < lastEnd + 0.05;
      for (const prm of [...oscs.map(o => o.frequency), amp.gain, vibG.gain]) hold(prm, t);
      for (const o of oscs) {
        if (legato) o.frequency.setTargetAtTime(f, t, p.glide);
        else if (ooh && r() < p.scoop) { o.frequency.setValueAtTime(f * Math.pow(2, -80 / 1200), t); o.frequency.setTargetAtTime(f, t + 0.01, 0.06); }
        else o.frequency.setValueAtTime(f, t);
      }
      amp.gain.setTargetAtTime(A, t, legato ? 0.03 : ooh ? 0.045 : 0.025);
      amp.gain.setTargetAtTime(0.85 * A, t + 0.12, 0.5);
      amp.gain.setTargetAtTime(0, t + dur, ooh ? 0.16 : 0.12);
      vibG.gain.setTargetAtTime(0, t, 0.05);
      if (dur > 0.35) vibG.gain.setTargetAtTime(p.vib, t + (ooh ? 0.25 : 0.3), 0.1);
      if (br.g) {
        const g = br.g.gain;
        hold(g, t);
        g.setTargetAtTime((legato ? 0.04 : 0.09) * A, t, 0.012); g.setTargetAtTime(0.03 * A, t + 0.06, 0.08);
        g.setTargetAtTime(0, t + dur, 0.12);
      }
      lastEnd = t + dur;
    },
    dispose(t) { P.dispose(t); },
  };
}
